import {useState, useEffect} from "react"
import {useUser} from "@/contexts/UserContextProvider"
import {
  UserDashboardData,
  OrgIntegrationRequirement,
} from "@/types/database.types"
import supabase from "@/supabaseConfig"

export function useOrgUsers() {
  const {user, orgId} = useUser()
  const [users, setUsers] = useState<UserDashboardData[] | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchUsers = async () => {
    if (!orgId) return

    try {
      setLoading(true)
      setError(null)

      const {data, error: fetchError} = await supabase
        .from("user_dashboard")
        .select("*")
        .eq("org_id", orgId)

      if (fetchError) {
        throw new Error(`Failed to fetch org users: ${fetchError.message}`)
      }

      setUsers(data as UserDashboardData[])
    } catch (err) {
      console.error("Error fetching org users:", err)
      setError(err instanceof Error ? err.message : "Unknown error")
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchUsers()
  }, [orgId])

  return {
    users,
    loading,
    error,
    refetch: fetchUsers,
  }
}

export function useCurrentUserData() {
  const {user, orgId} = useUser()
  const [currentUserData, setCurrentUserData] =
    useState<UserDashboardData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchCurrentUserDataHandler = async () => {
    if (!orgId || !user?.id) return

    try {
      setLoading(true)
      setError(null)

      const {data, error: fetchError} = await supabase
        .from("user_dashboard")
        .select("*")
        .eq("org_id", orgId)
        .eq("auth_user_id", user.id)
        .single()

      if (fetchError) {
        throw new Error(
          `Failed to fetch current user data: ${fetchError.message}`
        )
      }

      setCurrentUserData(data as UserDashboardData)
    } catch (err) {
      console.error("Error fetching current user data:", err)
      setError(err instanceof Error ? err.message : "Unknown error")
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchCurrentUserDataHandler()
  }, [orgId, user?.id])

  const isAdmin = currentUserData?.role === "ADMIN"

  // Update user settings function
  const updateUserSettings = async (
    authUserId: string,
    updates: {
      sensitivityReadLevel?: number
      role?: string
      isWhitelisted?: boolean
    }
  ) => {
    if (!orgId) {
      throw new Error("Organization ID is required")
    }

    const {error} = await supabase
      .from("org_users")
      .update({
        sensitivity_read_level: updates.sensitivityReadLevel,
        role: updates.role,
        is_whitelisted: updates.isWhitelisted,
      })
      .eq("auth_user_id", authUserId)
      .eq("org_id", orgId)

    if (error) {
      throw new Error(`Failed to update user settings: ${error.message}`)
    }
  }

  return {
    currentUserData,
    isAdmin,
    loading,
    error,
    refetch: fetchCurrentUserDataHandler,
    updateUserSettings,
  }
}

// Specialized hook for authentication that doesn't require orgId
// This mimics the old GraphQL useGetMyUserDashboard behavior
export function useAuthUserData(authUserId?: string) {
  const [userData, setUserData] = useState<UserDashboardData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchUserData = async () => {
    if (!authUserId) return

    try {
      setLoading(true)
      setError(null)

      const {data, error: fetchError} = await supabase
        .from("user_dashboard")
        .select("*")
        .eq("auth_user_id", authUserId)
        .maybeSingle() // Use maybeSingle to handle cases where user might not exist yet

      if (fetchError) {
        throw new Error(`Failed to fetch user data: ${fetchError.message}`)
      }

      setUserData(data as UserDashboardData)
    } catch (err) {
      console.error("Error fetching auth user data:", err)
      setError(err instanceof Error ? err.message : "Unknown error")
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchUserData()
  }, [authUserId])

  return {
    data: userData,
    loading,
    error,
    refetch: fetchUserData,
  }
}

// Hook to get org integration requirements by directly querying the table
export function useOrgIntegrationRequirements() {
  const {orgId} = useUser()
  const [requirements, setRequirements] = useState<
    OrgIntegrationRequirement[] | null
  >(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchRequirements = async () => {
    if (!orgId) return

    try {
      setLoading(true)
      setError(null)

      const {data, error: fetchError} = await supabase
        .from("org_integration_requirements")
        .select("*")
        .eq("org_id", orgId)
        .eq("is_enabled", true)

      if (fetchError) {
        throw new Error(
          `Failed to fetch integration requirements: ${fetchError.message}`
        )
      }

      setRequirements(data || [])
    } catch (err) {
      console.error("Error fetching integration requirements:", err)
      setError(err instanceof Error ? err.message : "Unknown error")
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchRequirements()
  }, [orgId])

  return {
    requirements,
    loading,
    error,
    refetch: fetchRequirements,
  }
}
